import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Loader2 } from 'lucide-react';

function AuthCallback({ onLogin }) {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('processing'); // processing, error
  const [errorMessage, setErrorMessage] = useState('');
  const [errorDetails, setErrorDetails] = useState('');

  useEffect(() => {
    // Prevent double processing in React StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        console.log('AuthCallback: Processing hash:', hash ? hash.substring(0, 30) + '...' : 'empty');
        
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          const error = params.get('error');
          const errorDesc = params.get('error_description');
          
          if (error) {
            console.error('AuthCallback: OAuth error:', error, errorDesc);
            setStatus('error');
            setErrorMessage(errorDesc || error || 'Google authentication was cancelled');
            setErrorDetails(`Error code: ${error}`);
            setTimeout(() => navigate('/login'), 4000);
            return;
          }
          
          console.error('AuthCallback: No session_id found in URL');
          setStatus('error');
          setErrorMessage('No authentication session found. Please try again.');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        console.log('AuthCallback: Exchanging session_id for user data...');
        
        // Exchange session_id for user data via backend proxy
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        let userData;
        
        // Retry logic for Safari/iOS which can fail on first attempt after redirect chain
        const maxRetries = 3;
        let lastError = null;
        
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            // Small delay before first attempt - lets Safari settle after redirect
            if (attempt === 1) {
              await new Promise(resolve => setTimeout(resolve, 300));
            } else {
              // Exponential backoff for retries
              await new Promise(resolve => setTimeout(resolve, attempt * 500));
            }
            
            console.log(`AuthCallback: Attempt ${attempt}/${maxRetries} to fetch session data`);
            
            // IMPORTANT: Do NOT use credentials: 'include' here
            // Safari ITP blocks credentialed requests after cross-origin redirect chains
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000);
            
            const response = await fetch(
              `${backendUrl}/api/auth/oauth/session-data?session_id=${sessionId}`,
              {
                method: 'GET',
                signal: controller.signal
              }
            );
            
            clearTimeout(timeoutId);

            if (!response.ok) {
              const errorText = await response.text();
              console.error(`AuthCallback: Session data fetch failed (attempt ${attempt}):`, response.status, errorText);
              lastError = new Error(`Session expired or invalid (${response.status})`);
              
              // Don't retry on 4xx errors (session issues, not network issues)
              if (response.status >= 400 && response.status < 500) {
                break;
              }
              continue;
            }

            userData = await response.json();
            console.log('AuthCallback: Got user data for:', userData.email);
            lastError = null;
            break; // Success - exit retry loop
            
          } catch (fetchError) {
            console.error(`AuthCallback: Fetch error (attempt ${attempt}):`, fetchError.message);
            lastError = fetchError;
            
            // If aborted (timeout), try again
            if (fetchError.name === 'AbortError') {
              console.log('AuthCallback: Request timed out, retrying...');
              continue;
            }
            
            // "Load failed" is Safari's generic network error - retry
            if (fetchError.message === 'Load failed' || fetchError.message === 'Failed to fetch') {
              console.log('AuthCallback: Network error (Safari), retrying...');
              continue;
            }
            
            // Unknown error - still retry
            continue;
          }
        }
        
        // If all retries failed
        if (!userData) {
          console.error('AuthCallback: All attempts to fetch session data failed:', lastError);
          setStatus('error');
          setErrorMessage('Failed to verify Google authentication');
          setErrorDetails(
            lastError?.message === 'Load failed' 
              ? 'Network error — please check your connection and try again. If using Safari, try disabling content blockers.'
              : (lastError?.message || 'Network error — please try again')
          );
          setTimeout(() => navigate('/login'), 5000);
          return;
        }

        // Create/update user in our backend
        console.log('AuthCallback: Authenticating with backend...');
        
        try {
          const userResponse = await fetch(`${backendUrl}/api/auth/google`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              email: userData.email,
              name: userData.name,
              picture: userData.picture,
              session_token: userData.session_token
            })
          });

          if (!userResponse.ok) {
            const errorText = await userResponse.text();
            console.error('AuthCallback: Backend auth failed:', userResponse.status, errorText);
            throw new Error(`Backend error (${userResponse.status})`);
          }

          const user = await userResponse.json();
          console.log('AuthCallback: Successfully authenticated user:', user.email, 'is_new_user:', user.is_new_user);

          // Store user data and login
          localStorage.setItem('user', JSON.stringify(user));
          onLogin(user);

          // Check if there's a pending invite to handle (team member invitation)
          const pendingInviteToken = localStorage.getItem('pendingInviteToken');
          if (pendingInviteToken) {
            console.log('AuthCallback: Found pending invite token, redirecting to accept invite...');
            localStorage.removeItem('pendingInviteToken');
            localStorage.setItem('autoAcceptInvite', 'true'); // Auto-accept on redirect
            window.history.replaceState(null, '', `/accept-invite?token=${pendingInviteToken}`);
            navigate(`/accept-invite?token=${pendingInviteToken}`, { replace: true });
          } else if (user.is_new_user) {
            // New Google OAuth signup - require payment before accessing app
            console.log('AuthCallback: New user detected, redirecting to payment setup...');
            window.history.replaceState(null, '', '/complete-signup');
            navigate('/complete-signup', { replace: true });
          } else {
            // Existing user - clear the URL fragment and redirect to dashboard
            window.history.replaceState(null, '', '/dashboard');
            navigate('/dashboard', { replace: true });
          }
        } catch (backendError) {
          console.error('AuthCallback: Backend auth error:', backendError);
          setStatus('error');
          setErrorMessage('Failed to complete sign-in');
          setErrorDetails(backendError.message || 'Server error — please try again');
          setTimeout(() => navigate('/login'), 4000);
        }

      } catch (error) {
        console.error('AuthCallback: Error during auth:', error);
        setStatus('error');
        setErrorMessage(error.message || 'Authentication failed. Please try again.');
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    processAuth();
  }, [navigate, onLogin]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
      <div className="text-center max-w-md px-6">
        {status === 'processing' ? (
          <>
            <div className="flex justify-center mb-4">
              <div className="p-4 bg-blue-600 rounded-xl">
                <Loader2 className="w-12 h-12 text-white animate-spin" />
              </div>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Signing you in...</h2>
            <p className="text-blue-200">Please wait while we complete authentication</p>
          </>
        ) : (
          <>
            <div className="flex justify-center mb-4">
              <div className="p-4 bg-red-600 rounded-xl">
                <AlertCircle className="w-12 h-12 text-white" />
              </div>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Authentication Error</h2>
            <p className="text-red-300 mb-2">{errorMessage}</p>
            {errorDetails && (
              <p className="text-red-400 text-sm mb-4 bg-red-950/50 p-2 rounded">{errorDetails}</p>
            )}
            <p className="text-blue-200 text-sm">Redirecting to login...</p>
          </>
        )}
      </div>
    </div>
  );
}

export default AuthCallback;
